import { createFileRoute, Link, redirect } from "@tanstack/react-router";
import { Car, Calendar, CheckCircle, Clock, Trophy } from "lucide-react";
import { getUserCars } from "@/lib/server/registration";
import { getSession } from "@/lib/auth-session";

export const Route = createFileRoute("/dashboard")({
	beforeLoad: async () => {
		const session = await getSession();
		if (!session?.user) {
			throw redirect({ to: "/login", search: { redirect: "/dashboard" } });
		}
		return { session };
	},
	loader: () => getUserCars(),
	component: DashboardPage,
});

function DashboardPage() {
	const userCars = Route.useLoaderData();

	return (
		<div className="min-h-screen bg-background p-4 sm:p-6">
			<div className="max-w-3xl mx-auto">
				<h1 className="text-4xl text-foreground mb-2">My Races</h1>
				<p className="text-muted-foreground mb-6">
					Your registered cars and race results
				</p>

				{userCars.length === 0 ? (
					<div className="bg-card rounded-xl p-12 text-center">
						<Car size={48} className="text-muted-foreground mx-auto mb-4" />
						<p className="text-foreground text-lg mb-2">
							No registrations yet
						</p>
						<p className="text-muted-foreground mb-6">
							Find an event and register your car to get started.
						</p>
						<Link
							to="/events"
							className="inline-flex items-center gap-2 px-6 py-3 bg-primary hover:bg-accent text-primary-foreground font-bold rounded-lg transition-colors uppercase text-sm"
						>
							<Trophy size={16} />
							Browse Events
						</Link>
					</div>
				) : (
					<div className="space-y-4">
						{userCars.map(({ car, eventName, eventDate, eventStatus, eventLocation }) => {
							const isLive =
								eventStatus === "qualifying" ||
								eventStatus === "finals";
							return (
								<Link
									key={car.id}
									to="/events/$eventId"
									params={{ eventId: String(car.eventId) }}
									className="block bg-card rounded-xl p-5 hover:border-primary/40 transition-all"
								>
									<div className="flex items-start justify-between mb-3">
										<div>
											<h2 className="text-lg text-foreground font-bold">
												{eventName}
											</h2>
											<p className="text-muted-foreground text-sm flex items-center gap-1">
												<Calendar size={14} />
												{eventDate}
												{eventLocation && ` · ${eventLocation}`}
											</p>
										</div>
										<span
											className={`px-2 py-0.5 rounded-full text-xs font-bold uppercase ${
												isLive
													? "bg-success text-foreground animate-pulse"
													: eventStatus === "complete"
														? "bg-muted text-muted-foreground"
														: "bg-primary/20 text-primary"
											}`}
										>
											{eventStatus}
										</span>
									</div>

									<div className="flex items-center gap-4 bg-background rounded-lg px-4 py-3">
										{car.avatarUrl ? (
											<img
												src={car.avatarUrl}
												alt=""
												className="w-12 h-12 rounded-lg object-cover shrink-0"
											/>
										) : (
											<div className="w-12 h-12 rounded-lg bg-muted flex items-center justify-center text-muted-foreground font-bold shrink-0">
												{car.carName?.charAt(0) ?? "#"}
											</div>
										)}
										<div className="flex-1 min-w-0">
											<p className="text-foreground font-bold">
												<span className="text-xs text-primary bg-primary/10 rounded px-1 py-0.5 mr-1">
													#{car.carNumber}
												</span>
												{car.carName ?? "Unnamed"}
											</p>
											<p className="text-muted-foreground text-sm">
												{car.driverName}
											</p>
										</div>
										<div className="shrink-0 flex items-center gap-1 text-sm">
											{car.checkedIn ? (
												<span className="flex items-center gap-1 text-success">
													<CheckCircle size={14} />
													Checked in
												</span>
											) : (
												<span className="flex items-center gap-1 text-muted-foreground">
													<Clock size={14} />
													Pending
												</span>
											)}
										</div>
									</div>
								</Link>
							);
						})}
					</div>
				)}
			</div>
		</div>
	);
}
